import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Button,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  Send,
  Close,
  Person,
  SupportAgent,
  CheckCircle,
  Chat
} from '@mui/icons-material';

export default function ChatDialog({ open, onClose, user, onMarkAsRead }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const messagesEndRef = useRef(null);

  // Memoized scroll function
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: "smooth",
        block: "nearest"
      });
    });
  }, []);

  // Optimized scroll effect
  useEffect(() => {
    if (open && selectedConversation?.messages?.length > 0) {
      const timer = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedConversation?.messages, open, scrollToBottom]);

  // Notify parent when chat opens
  useEffect(() => {
    if (open && onMarkAsRead) {
      onMarkAsRead();
    }
  }, [open, onMarkAsRead]);

  // Optimized conversation messages fetch
  const fetchConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://propertyearn.net/api/api/chat/conversations/${conversationId}/messages`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        setSelectedConversation(prev => {
          if (!prev || prev.conversation_id !== conversationId) return prev;
          
          const currentMessages = prev.messages || [];
          const newMessages = data.messages || [];
          
          // Quick length check first for performance
          if (currentMessages.length !== newMessages.length) {
            return { ...prev, messages: newMessages };
          }
          
          return prev;
        });
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, []);

  // Fast conversation fetch - minimal data
  const fetchConversationsFast = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/chat/conversations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const conversationsData = data.conversations || [];
        
        setConversations(conversationsData);
        return conversationsData;
      }
    } catch (error) {
      console.error('Fast conversation fetch error:', error);
    }
    return [];
  }, []);

  // Initial load with progress indicator
  const fetchConversationsWithProgress = useCallback(async () => {
    try {
      setLoading(true);
      const conversationsData = await fetchConversationsFast();
      
      // Auto-select first active conversation
      if (conversationsData.length > 0 && !selectedConversation) {
        const firstActiveConv = conversationsData.find(conv => !conv.is_resolved) || conversationsData[0];
        setSelectedConversation({
          ...firstActiveConv,
          messages: []
        });
        // Load messages in background without blocking UI
        setTimeout(() => fetchConversationMessages(firstActiveConv.conversation_id), 100);
      }
      
      setInitialLoad(false);
    } catch (error) {
      console.error('Conversation load error:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchConversationsFast, fetchConversationMessages, selectedConversation]);

  // Load conversations when dialog opens
  useEffect(() => {
    if (open) {
      if (initialLoad) {
        fetchConversationsWithProgress();
      } else {
        // Subsequent opens use fast load
        fetchConversationsFast();
      }
    }
  }, [open, initialLoad, fetchConversationsWithProgress, fetchConversationsFast]);

  // Optimized polling
  useEffect(() => {
    if (!open) return;

    let currentConversationId = selectedConversation?.conversation_id;
    
    const interval = setInterval(async () => {
      try {
        if (currentConversationId) {
          await fetchConversationMessages(currentConversationId);
        }
        // Silent conversation refresh - no state update if no changes
        await fetchConversationsFast();
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [open, selectedConversation?.conversation_id, fetchConversationMessages, fetchConversationsFast]);

  // Optimized message sending
  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedConversation || selectedConversation.is_resolved) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `https://propertyearn.net/api/api/chat/conversations/${selectedConversation.conversation_id}/send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: newMessage.trim()
          })
        }
      );

      if (response.ok) {
        setNewMessage('');
        // Fast refresh of current conversation only
        await fetchConversationMessages(selectedConversation.conversation_id);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      alert('Error sending message');
    } finally {
      setSending(false);
    }
  }, [newMessage, selectedConversation, fetchConversationMessages]);

  // Optimized conversation selection
  const handleSelectConversation = useCallback(async (conversation) => {
    if (selectedConversation?.conversation_id === conversation.conversation_id) return;

    setSelectedConversation({
      ...conversation,
      messages: []
    });

    // Load messages after state update
    setTimeout(() => fetchConversationMessages(conversation.conversation_id), 50);
  }, [selectedConversation, fetchConversationMessages]);

  // Key press handler
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // Time formatter
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // Skeleton loader for conversations
  const ConversationSkeleton = () => (
    <Box sx={{ p: 2 }}>
      {[...Array(5)].map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={32} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ height: 20, bgcolor: 'grey.300', borderRadius: 1, mb: 1, width: '80%' }} />
            <Box sx={{ height: 16, bgcolor: 'grey.200', borderRadius: 1, width: '60%' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          maxHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: 'primary.main',
        color: 'white'
      }}>
        <Typography variant="h6">Support Chat</Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex' }}>
        {/* Conversations List - Optimized */}
        <Box sx={{ 
          width: 300, 
          borderRight: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Conversations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loading ? 'Loading...' : `${conversations.length} total`}
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {loading && initialLoad ? (
              <ConversationSkeleton />
            ) : conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Chat sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No conversations yet
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    onClose();
                    if (window.location.pathname !== '/help') {
                      window.location.href = '/help#contact-form';
                    }
                  }}
                  sx={{ mt: 1 }}
                >
                  Start New Conversation in Help Center Page
                </Button>
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {conversations.map((conversation) => (
                  <ListItem
                    key={conversation.conversation_id}
                    button
                    selected={selectedConversation?.conversation_id === conversation.conversation_id}
                    onClick={() => handleSelectConversation(conversation)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                      },
                      backgroundColor: conversation.is_resolved ? 'grey.50' : 'transparent',
                      py: 1.5,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: conversation.is_resolved ? 'grey.500' : 
                                 conversation.has_admin_response ? 'success.main' : 'primary.main',
                        width: 32,
                        height: 32
                      }}>
                        {conversation.is_resolved ? '✓' : 
                         conversation.has_admin_response ? 'S' : 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ 
                          textDecoration: conversation.is_resolved ? 'line-through' : 'none',
                          color: conversation.is_resolved ? 'text.secondary' : 'text.primary',
                          fontSize: '0.875rem'
                        }}>
                          {conversation.initial_message?.substring(0, 25) || 'New chat'}...
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" noWrap>
                          {conversation.is_resolved ? 'Resolved' : 
                           conversation.last_sender_type === 'admin' ? 'Support' : 
                           conversation.last_sender_type === 'user' ? 'You' : 'Waiting'} • 
                          {formatTime(conversation.last_activity)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Chat Area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedConversation ? (
            <>
              {/* Messages Area */}
              <Box sx={{ 
                flexGrow: 1, 
                p: 2, 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                {selectedConversation.is_resolved ? (
                  <Box sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Conversation Resolved
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                      This conversation has been resolved. Start a new conversation for further assistance.
                    </Typography>
                    
                    {selectedConversation.messages && selectedConversation.messages.length > 0 && (
                      <Box sx={{ width: '100%', mb: 3 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Conversation History:
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                          {selectedConversation.messages.map((message) => (
                            <Box key={message.id} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {message.sender_name}: 
                              </Typography>
                              <Typography variant="body2">
                                {message.message}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Box>
                    )}
                    
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        onClose();
                        if (window.location.pathname !== '/help') {
                          window.location.href = '/help#contact-form';
                        }
                      }}
                      sx={{ mt: 2 }}
                    >
                      Start New Conversation in Help Center Page
                    </Button>
                  </Box>
                ) : selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                  selectedConversation.messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.sender_type === 'user' ? 'flex-end' : 'flex-start',
                        mb: 1
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        maxWidth: '70%',
                        flexDirection: message.sender_type === 'user' ? 'row-reverse' : 'row'
                      }}>
                        <Avatar sx={{ 
                          width: 32, 
                          height: 32, 
                          mx: 1,
                          bgcolor: message.sender_type === 'user' ? 'primary.main' : 'success.main'
                        }}>
                          {message.sender_type === 'user' ? <Person /> : <SupportAgent />}
                        </Avatar>
                        <Paper
                          sx={{
                            p: 1.5,
                            backgroundColor: message.sender_type === 'user' ? 'primary.main' : 'grey.100',
                            color: message.sender_type === 'user' ? 'white' : 'text.primary',
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="body2">{message.message}</Typography>
                          <Typography variant="caption" sx={{ 
                            display: 'block', 
                            mt: 0.5,
                            opacity: 0.7,
                            color: message.sender_type === 'user' ? 'white' : 'text.secondary'
                          }}>
                            {formatTime(message.timestamp)}
                            {message.sender_name && message.sender_type === 'admin' && ` • ${message.sender_name}`}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    p: 3
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              <Divider />
              
              {/* Input Area */}
              <Box sx={{ p: 2 }}>
                {selectedConversation.is_resolved ? (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This conversation has been resolved.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      onClick={() => {
                        onClose();
                        if (window.location.pathname !== '/help') {
                          window.location.href = '/help#contact-form';
                        }
                      }}
                    >
                      Start New Conversation in Help Center Page
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      size="small"
                      multiline
                      maxRows={3}
                    />
                    <IconButton 
                      onClick={sendMessage} 
                      disabled={!newMessage.trim() || sending}
                      color="primary"
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {sending ? <CircularProgress size={24} /> : <Send />}
                    </IconButton>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3
            }}>
              <SupportAgent sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a conversation
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}